<template>
  <div class="px-4 py-2 flex flex-col gap-y-2">
    <h1 class="text-h1">Dépannage signature certifiée</h1>
    <div>
      <p>
        Bienvenue sur la page d'aide à la signature certifiée. Afin de vous aider au mieux nous devons vous présenter
        quelques notions clés avant d'aller plus loin :
      </p>
      <ul>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          La signature certifiée repose sur un logiciel,
          <span class="font-bold">Smart Card Middleware Desktop,</span>
          installé sur votre ordinateur que nous appellerons pour toute la suite de l'article
          <span class="font-bold">Smart Card Manager</span>
          (Version 6.23.50.5 ou plus récente)
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          <span class="font-bold ml-1v">Smart Card Manager</span> est responsable de détecter les certificats de
          signature, qu'ils proviennent d'une carte agent ou qu'ils soient dématérialisés
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Une première famille d'erreurs est liée à la détection de certificat par
          <span class="font-bold">Smart Card Manager</span>, ou des problèmes de communication avec
          <span class="font-bold">Smart Card Manager</span>
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Une seconde famille d'erreurs à la signature est liée aux communications réseau qui ont lieu lors de la
          signature certifiée. Le Parapheur a besoin d'un environnement réseau stable et bien fonctionnel pour procéder
          à la signature
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Enfin, très rarement il arrive parfois que certains documents sont incompatibles avec la signature certifiée
        </li>
      </ul>
      <PCallout
        class="ml-2 mt-2"
        title="La signature certifiée repose sur un logiciel installé sur votre
          ordinateur, Smart Card Manager (Version 6.23.50.5 ou plus récente)"
      />
    </div>
    <div>
      <h2 class="text-h2">Causes courantes</h2>
      <p>
        Voici une liste des causes les plus communément rencontrées provoquant une erreur lors de la signature certifiée
        :
      </p>
      <ul>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Certificat non détecté
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Changement d'état réseau (VPN éteint puis rallumé)
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Perte de réseau, par exemple déplacement d'un bureau à l'autre, du bureau à chez soi
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Mise en veille de l'ordinateur
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Onglet inactif depuis longtemps
        </li>
      </ul>
    </div>
    <div>
      <h2 class="text-h2">Vérifications simples avant de poursuivre</h2>
      <p>Avant de poursuivre, veuillez procéder à un second essai de signature comme suit :</p>
      <ul>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          D'abord rafraichissez la page du Parapheur en appuyant sur la touche F5 de votre clavier.
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Essayez d'effectuer une signature certifiée simple sur un seul document.
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Si le problème persiste, poursuivez la lecture
        </li>
      </ul>
    </div>
    <div class="flex flex-col gap-y-1">
      <h2 class="text-h2">Détection du certificat</h2>
      <p>
        Nous devons d'abord nous assurer que
        <span class="font-bold">Smart Card Manager</span> détecte bien votre certificat. Pour ce faire, procédez comme
        suit :
      </p>
      <ul>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Trouvez l'icône <span class="font-bold">de Smart Card Manager</span> dans la barre d'état en bas à droite de
          votre écran
        </li>
        <li class="">
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          <span>Elle devrait avoir l'apparence suivante, avec ou sans la coche verte :</span>
          <img src="/middleware-status-bar-icon.png" class="object-cover inline ml-1v" />
        </li>
        <li>
          <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
          Cliquez dessus et comparez le résultat avec les captures d'écran ci dessous :
        </li>
      </ul>
      <div class="flex flex-col lg:flex-row gap-2">
        <HelpImage
          img="/middleware-card-found-statusbar.png"
          label="Certificat correctement détecté"
          labelBgColor="success"
        />
        <HelpImage img="/middleware-no-card-statusbar-1.png" label="Certificat non détecté" labelBgColor="error" />
      </div>
      <div class="flex flex-col gap-y-1">
        <h3 class="text-h3">Certificat non détecté</h3>
        <ul>
          <li>
            <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
            Il arrive parfois que la carte se déchausse légèrement du lecteur. Vérifiez que votre carte agent est bien
            enfoncée au bout du lecteur de carte
          </li>
          <li>
            <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
            Si vous utilisez un lecteur de carte externe, essayez de le débrancher et le rebrancher à nouveau
          </li>
          <li>
            <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
            Dans les deux cas attendez un instant et consultez à nouveau
            <span class="font-bold">Smart Card Manager</span> pour voir si le certificat est correctement détecté.
          </li>
        </ul>
      </div>
      <div class="flex flex-col gap-y-1">
        <h3 class="text-h3">Certificat détecté</h3>
        <p>
          Si le problème persiste alors que le certificat est bien détecté, nous allons redémarrer
          <span class="font-bold">Smart Card Manager</span>. En effet, il arrive parfois que ce dernier soit planté et
          dans ce cas un simple redémarrage du programme résout les problèmes de signature.
        </p>
        <div>
          <p>
            Suivez les instructions montrées sur les captures d'écran pour redémarrer
            <span class="font-bold">Smart Card Manager</span> :
          </p>
          <div class="flex flex-col lg:flex-row gap-2 mt-1">
            <HelpImage
              img="/middleware-exit-status-bar.png"
              label="Étape 1, arrêter Smart Card Manager en choisissant 'quitter' via l'icône dans la barre d'état"
            />
            <HelpImage
              img="/middleware-run.png"
              label="Étape 2, relancer Smart Card Manager en saisissant 'smart card manager' dans la recherche windows"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-y-1">
      <h2 class="text-h2">Si le problème persiste</h2>
      <p>
        Nous sommes désolés que malgré toutes les vérifications vous ne parveniez toujours pas à effectuer de signature
        certifiée. Il nous reste quelques dernières vérifications à effectuer avant de nous communiquer le problème.
      </p>
      <div>
        <h3 class="text-h3">Dernières vérifications</h3>
        <ul>
          <li>
            <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
            Signer un autre document. Il arrive rarement que certains documents soient dans un format pdf qui est
            incompatible avec la signature certifiée. En vérifiant la signature sur un autre document provenant d'une
            autre source nous aurons de la clareté sur ce point
          </li>
          <li>
            <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
            Signature du même document par un tiers. Demandez à un collègue de faire un essai dans un parapheur vierge,
            en y téléversant le document que vous n'arrivez pas à signer. Cela nous permettra de distinguer entre un
            problème lié au poste et un problème lié au document
          </li>
        </ul>
      </div>
      <div>
        <h3 class="text-h3">Nous communiquer le problème</h3>
        <div>
          <p>
            Pour nous communiquer un problème de signature persistant veillez à bien inclure les informations suivantes
            dans votre communication :
          </p>
          <ul>
            <li>
              <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
              Lien vers le parapheur ou numéro chrono du parapheur, ex:
              https://parapheur.din.gouv.fr/letter-file/2025090000003 ou 2025090000003
            </li>
            <li>
              <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
              Nom du document et nom du répertoire si applicable
            </li>
            <li>
              <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
              Certificat de type carte, dématérialisé ou clé
            </li>
            <li>
              <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
              Date et plage horaire à laquelle vous avez rencontré les erreurs
            </li>
            <li>
              <OhVueIcon :name="dotIcon" scale="1.5" class="pt-1v" />
              Message d'erreur rencontré
            </li>
          </ul>
          <p class="text-sm">
            PS : Vous pouvez trouver nos différents canaux de communication sur
            <PLink to="user-guide">la page suivante</PLink>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useIcons, PLink, PCallout } from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import { BiDot } from "oh-vue-icons/icons";
import HelpImage from "../components/HelpImage.vue";

const [dotIcon] = useIcons(BiDot);
</script>
